import { FC, useMemo } from 'react';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import useAdminBookings, { AdminBooking } from '@/api/useAdminBookings.ts';
import dayjs from 'dayjs';
import { formatCareType } from '@/utils/formatCareType.ts';

const columnHelper = createColumnHelper<AdminBooking>();

const AdminBookingsPage: FC = () => {
  const { data: bookings, isLoading, isError } = useAdminBookings();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-[#FFBE00]';
      case 'accepted':
        return 'text-[#00A96E]';
      case 'declined':
        return 'text-[#FF5861]';
      case 'completed':
        return 'text-[#00A96E]';
      case 'not_completed':
        return 'text-[#FF5861]';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'not_completed':
        return 'Not Completed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('user_email', {
        header: 'Family',
        cell: (info) => (
          <div className="font-semibold">
            {info.getValue()?.split('@')[0] || 'Unknown'}
          </div>
        ),
      }),
      columnHelper.accessor('carer_email', {
        header: 'Carer',
        cell: (info) => (
          <div className="font-semibold">
            {info.getValue()?.split('@')[0] || 'Unknown'}
          </div>
        ),
      }),
      columnHelper.accessor('care_type', {
        header: 'Care Type',
        cell: (info) => formatCareType(info.getValue()),
      }),
      columnHelper.accessor('start_date', {
        header: 'Date & Time',
        cell: (info) => {
          const row = info.row.original;
          return dayjs(info.getValue()).format('MMM DD, YYYY') + 
            (row.start_time ? ` — ${row.start_time}` : '');
        },
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => (
          <span className={`font-semibold ${getStatusColor(info.getValue())}`}>
            {getStatusLabel(info.getValue())}
          </span>
        ),
      }),
    ],
    []
  );

  return (
    <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Lite admin dashboard for daily management</h1>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : isError ? (
        <div className="bg-red-50 rounded-lg p-8 text-center">
          <p className="text-red-500">Error loading bookings</p>
        </div>
      ) : bookings && bookings.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500">No bookings found</p>
        </div>
      ) : (
        <DataTable data={bookings || []} columns={columns} className="min-w-[600px]" />
      )}
    </div>
  );
};

export default AdminBookingsPage;
